import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export type WatchEntityType = 'task' | 'project';

export interface PmWatcher {
  id: string;
  organization_id: string;
  entity_type: WatchEntityType;
  entity_id: string;
  user_id: string;
  created_at: string;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/watchers`;

export const pmWatchersApiService = {
  async list(params: { organization_id: string; entity_type: WatchEntityType; entity_id: string }): Promise<{ data: PmWatcher[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to list watchers' };
      }

      return { data: (result.data || []) as PmWatcher[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async me(params: { organization_id: string; entity_type: WatchEntityType; entity_id: string }): Promise<{ data: { watching: boolean } | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
      });

      const response = await fetch(`${API_BASE_URL}/me?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to check watcher status' };
      }

      return { data: (result.data || null) as { watching: boolean } | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async watch(input: { organization_id: string; entity_type: WatchEntityType; entity_id: string }): Promise<{ data: PmWatcher | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to watch' };
      }

      return { data: (result.data || null) as PmWatcher | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async unwatch(params: { organization_id: string; entity_type: WatchEntityType; entity_id: string }): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to unwatch' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
